/**
 * tm/pages/MobileRemind.tsx — 휴대폰 홈 「내일 동행상담 확인」 (2026-10-28 지도사 「클릭 한 번으로 · 직원은 복사해 자기 카톡으로」)
 *  내일 잡힌 동행상담이 없으면 아무것도 안 보임 · 줄마다 [문자](지도사 이름 · 번호로 담당 팀장님께 바로) · [카톡](휴대폰 공유 → 안 되면 복사)
 *  줄을 누르면 판: 받는 분 고르기 · 글 고치기 · 보내기
 */
import { useCallback, useEffect, useState } from 'react';
import { G, clamp } from '../portal-ui/tds';
import { Button, Card, CardHead, Check, Sheet, TOSS } from './mobileKit';
import { copyText, mainTarget, remindCopied, remindLine, remindList, remindSend, shareText } from '../consultRemind';
import type { RemindItem } from '../consultRemind';

export default function MobileRemind({ show }: { show: (m: string) => void }) {
  const [list, setList] = useState<RemindItem[]>([]);
  const [sheet, setSheet] = useState<RemindItem | null>(null);
  const [keys, setKeys] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => { const r = await remindList().catch(() => null); setList(r && r.data ? r.data.list : []); }, []);
  useEffect(() => { void load(); }, [load]);
  if (!list.length) return null;

  const send = async (it: RemindItem, k?: string[], t?: string) => {
    if (busy) return;
    setBusy(it.id);
    try {
      const r = await remindSend(it.id, { keys: k, text: t });
      if (!r.success || !r.data) throw new Error(r.error || '보내지 못했어요');
      const okN = r.data.remind.results.filter(x => x.sent).length;
      show(okN ? '팀장님께 확인 문자를 보냈어요' : `못 보냈어요 · ${(r.data.remind.results[0] || { error: '' }).error || '다시 해 주세요'}`);
      setSheet(null); await load();
    } catch (e) { show(e instanceof Error ? e.message : '보내지 못했어요'); }
    finally { setBusy(''); }
  };
  const kakao = async (it: RemindItem, body: string) => {
    if (await shareText(body)) { void remindCopied(it.id).then(load); return; }
    if (await copyText(body)) { show('복사했어요 · 카톡에 붙여 넣어 보내 주세요'); void remindCopied(it.id).then(load); return; }
    open(it); show('아래 글을 길게 눌러 복사해 주세요');
  };
  const open = (it: RemindItem) => { const m = mainTarget(it); setSheet(it); setKeys(m ? [m.key] : []); setText(m ? m.sms : ''); };

  return (
    <Card pad="4px 0 10px" m="8px 0 18px">
      <CardHead title="📞 내일 동행상담 확인" count={list.length} />
      {list.map(it => {
        const main = mainTarget(it);
        const line = remindLine(it.remind, it.targets);
        return (
          <div key={it.id} style={{ padding: '10px 20px', borderTop: `1px solid ${TOSS.line}` }}>
            <button type="button" onClick={() => open(it)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: TOSS.text, ...clamp(1) }}>{it.company_name}</div>
              <div style={{ fontSize: 15, color: line && line.bad ? TOSS.red : TOSS.sub, marginTop: 2, ...clamp(2) }}>
                {[it.time, main ? main.honor : '받을 팀장님 없음', line ? line.text : ''].filter(Boolean).join(' · ')}
              </div>
            </button>
            {main && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {!it.remind && !main.why && <Button h={44} onClick={() => void send(it)}>{busy === it.id ? '보내는 중…' : '문자 보내기'}</Button>}
                <Button h={44} kind="weak" onClick={() => void kakao(it, main.copy)}>카톡으로</Button>
              </div>
            )}
          </div>
        );
      })}
      <Sheet open={!!sheet} onClose={() => setSheet(null)} title={sheet ? `${sheet.company_name} · 확인 연락` : ''}>
        {sheet && (() => {
          const main = mainTarget(sheet);
          return <>
            {sheet.targets.map(t => (
              <Check key={t.key} on={keys.includes(t.key)} onChange={v => { if (t.why) return; setKeys(k => (v ? [...k, t.key] : k.filter(x => x !== t.key))); }}>
                {`${t.honor}${t.why ? ` · ${t.why}` : ''}`}
              </Check>
            ))}
            <textarea value={text} onChange={e => setText(e.target.value)} rows={9} aria-label="보낼 글"
              style={{ width: '100%', marginTop: 10, padding: 12, borderRadius: 14, border: `1px solid ${G.g300}`, fontSize: 16, lineHeight: 1.6, color: G.g900, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            <div style={{ fontSize: 14, color: TOSS.faint, margin: '6px 0 12px' }}>문자는 지도사 이름 · 번호로 나가요 · 고치지 않으면 받는 분마다 이름을 넣어 보내요</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button kind="weak" onClick={() => void kakao(sheet, main && text === main.sms ? main.copy : text)}>카톡으로</Button>
              <Button muted={!keys.length} onClick={() => { if (!keys.length) { show('받을 분을 골라 주세요'); return; } void send(sheet, keys, main && text === main.sms ? undefined : text); }}>{busy ? '보내는 중…' : '문자 보내기'}</Button>
            </div>
          </>;
        })()}
      </Sheet>
    </Card>
  );
}
